import { useState, useCallback } from 'react';

import { GridFilterModel } from '@mui/x-data-grid-pro';

import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { DataGrid, Loader } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { ExportAsPDFModal } from '@/components/Modals/InternalAffairsModal/ExportAsPdf';
import { ExportReportModal } from '@/components/Modals/InternalAffairsModal/ExportReport';
import { DeleteInternalAffairsCaseModal } from '@/components/Modals/InternalAffairsModal/InternalAffairsDeleteModal';
import { SendEmailModal } from '@/components/Modals/InternalAffairsModal/SendEmail';
import { Button } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import { IIACase, IACaseType, IACaseStatus, FileOrigin } from '@/models';
import { InternalAffairsCasesTableColumns } from '@/pages/Tools/InternalAffairs/data';
import {
  formatCaseType,
  formatStatus,
  getUserDisplayName,
  generateSingleCasePDF,
} from '@/pages/Tools/InternalAffairs/ia.utils';
import { uploadFiles } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

// Transform API data to table format
const transformIACaseToTableRow = (iaCase: IIACase, index: number) => {
  const assignedOfficersNames = iaCase.assignedOfficers
    .map((officer) => getUserDisplayName(officer))
    .join(', ');

  const caseOwnerName = getUserDisplayName(iaCase.caseOwner);
  const createdByName = getUserDisplayName(iaCase.createdBy);

  return {
    id: iaCase._id,
    position: index + 1,
    caseId: iaCase.caseId,
    officersWithAccess: assignedOfficersNames,
    caseType: iaCase.caseType,
    status: iaCase.status,
    createdBy: createdByName,
    caseOwner: caseOwnerName,
    createdAt: iaCase.createdAt,
    updatedAt: iaCase.updatedAt,
    // Keep original data for modals
    _original: iaCase,
  };
};

const InternalAffairsTable: React.FC = () => {
  const [selectedCase, setSelectedCase] = useState<any>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExportPDFModalOpen, setIsExportPDFModalOpen] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [emailSending, setEmailSending] = useState(false);
  const [currentFilterModel, setCurrentFilterModel] =
    useState<GridFilterModel | null>(null);
  const { navigateWithSearch } = useSearchPersist();

  const { user } = useUserStore();
  const { updateToast } = useToastStore();

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `internalAffairsCases-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return InternalAffairsCasesTableColumns;
      }

      return mergeColumnSettings(
        InternalAffairsCasesTableColumns,
        savedSettings,
      );
    },
  );

  const buildFilterSummary = (filterModel: GridFilterModel | null): string => {
    if (!filterModel) return 'No filters applied';

    const fieldLabels: Record<string, string> = {
      caseId: 'Case ID',
      caseType: 'Case Type',
      status: 'Status',
      officersWithAccess: 'Officer(s) with access',
      createdBy: 'Created By',
      createdAt: 'Created At',
    };

    const parts: string[] = [];

    // Active column filters
    const activeItems = (filterModel.items || []).filter(
      (item) => item.value != null && item.value !== '',
    );
    if (activeItems.length > 0) {
      activeItems.forEach((item) => {
        const label = fieldLabels[item.field] || item.field;
        const value =
          formatCaseType(item.value as IACaseType) !== item.value
            ? formatCaseType(item.value as IACaseType)
            : formatStatus(item.value as IACaseStatus) !== item.value
            ? formatStatus(item.value as IACaseStatus)
            : item.value;
        parts.push(`${label}: ${value}`);
      });
    }

    // Quick filter (search bar)
    if (filterModel.quickFilterValues?.length) {
      parts.push(`Search: ${filterModel.quickFilterValues.join(' ')}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'No filters applied';
  };

  const generateReportData = useCallback(async () => {
    try {
      const params: { format: 'pdf'; filter?: string } = { format: 'pdf' };
      if (currentFilterModel) {
        params.filter = transformFilterForAPI(
          JSON.stringify(currentFilterModel),
        );
      }

      const response = await InternalAffairsApi.generateReport(params);
      const data = response.data;

      const statusColors: { [key: string]: string } = {
        OPEN: '#22C55D',
        IN_PROGRESS: '#F97315',
        CLOSED: '#9E9E9E',
      };

      const statusOrder = ['OPEN', 'IN_PROGRESS', 'CLOSED'];
      const statusData = data.statusDistribution
        .sort(
          (a, b) =>
            statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status),
        )
        .map((item) => ({
          name: formatStatus(item.status as IACaseStatus),
          value: item.count,
          color: statusColors[item.status] || '#90A4AE',
        }));

      return {
        generatedDate: new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        filters: buildFilterSummary(currentFilterModel),
        cases: data.cases.map((c) => ({
          caseId: c.caseId,
          caseType: formatCaseType(c.caseType as IACaseType),
          status: formatStatus(c.status as IACaseStatus),
          officer: c.caseOwner,
          createdBy: c.createdBy,
          createdAt: c.createdAt || 'N/A',
          lastUpdated: c.updatedAt || c.createdAt || 'N/A',
        })),
        statusData,
      };
    } catch (error) {
      console.error('Error generating report:', error);
      return null;
    }
  }, [currentFilterModel]);

  const columns = useDataGridColumns({
    type: 'internalAffairs',
    columnSettings,
    actionMethods: {
      iaActions: {
        onView: (row: any) => {
          if (row?.id) {
            navigateWithSearch(`/tools/internal-affairs/view/${row.id}`);
          }
        },
        onEdit: (row: any) => {
          if (row?.id) {
            navigateWithSearch(`/tools/internal-affairs/edit/${row.id}`);
          }
        },
        onExport: (row: any) => {
          setSelectedCase(row);
          setIsExportPDFModalOpen(true);
        },
        onEmail: (row: any) => {
          setSelectedCase(row);
          setIsSendEmailModalOpen(true);
        },
        onDelete: (row: any) => {
          setSelectedCase(row);
          setIsDeleteModalOpen(true);
        },
      },
    },
  });

  // Helper function to transform filter values for API compatibility
  const transformFilterForAPI = (filterStr: string): string => {
    if (!filterStr) return filterStr;

    try {
      const filterModel = JSON.parse(filterStr);

      if (filterModel.items) {
        filterModel.items = filterModel.items.map((item: any) => {
          let mapped = { ...item };

          // Map field names to dot-notation paths for populated references
          if (mapped.field === 'officersWithAccess') {
            mapped = { ...mapped, field: 'assignedOfficers.fullName' };
          } else if (mapped.field === 'createdBy') {
            mapped = { ...mapped, field: 'createdBy.fullName' };
          }

          // Map singleSelect operators to string equivalents the server understands
          if (mapped.operator === 'is') {
            mapped = { ...mapped, operator: 'equals' };
          } else if (mapped.operator === 'not') {
            mapped = { ...mapped, operator: 'doesNotEqual' };
          }

          return mapped;
        });
      }

      // Join multi-word quick filter values back into a single search term
      // MUI DataGrid splits "Emily Cooper" into ["Emily", "Cooper"] by default
      if (
        filterModel.quickFilterValues &&
        filterModel.quickFilterValues.length > 1
      ) {
        filterModel.quickFilterValues = [
          filterModel.quickFilterValues.join(' '),
        ];
      }

      return JSON.stringify(filterModel);
    } catch (e) {
      return filterStr;
    }
  };

  // Fetch IA cases from API
  const fetchIACases = async (
    page: number,
    pageSize: number,
    filter: string,
    sortParam?: string,
  ) => {
    try {
      const params: any = {
        page,
        limit: pageSize,
      };

      if (filter) {
        params.filter = transformFilterForAPI(filter);
      }

      if (sortParam) {
        params.sort = sortParam;
      }

      const response = await InternalAffairsApi.list(params);
      const { results, hasNextPage } = response.data;

      const transformedRows = results.map((iaCase, index) =>
        transformIACaseToTableRow(iaCase, (page - 1) * pageSize + index),
      );

      return {
        rows: transformedRows,
        totalCount: transformedRows.length,
        hasNextPage,
      };
    } catch (error) {
      console.error('Error fetching IA cases:', error);
      return {
        rows: [],
        totalCount: 0,
        hasNextPage: false,
      };
    }
  };

  return (
    <>
      <Loader open={emailSending} />
      <div className="pt-3">
        <DataGrid
          className="w-full"
          key={refreshKey}
          name="internalAffairs"
          autoPageSize={false}
          persistSearch
          fetchData={fetchIACases}
          columns={columns}
          columnSettings={columnSettings}
          sx={{
            width: 'calc(100% - 20px)',
            minHeight: 300,
          }}
          disableMultipleColumnsSorting={true}
          setColumnSettings={setColumnSettings}
          getRowId={(row) => row?.id || ''}
          onFilterModelChange={(model) => setCurrentFilterModel(model)}
          cta={
            <div className="flex gap-3">
              <Button
                type="button"
                className="rounded-md bg-white px-[20px]"
                variant="outline"
                onClick={async () => {
                  const data = await generateReportData();
                  setReportData(data);
                  setIsReportModalOpen(true);
                }}
              >
                Generate report
              </Button>
              <Button
                variant="default"
                className="rounded-md px-[20px]"
                onClick={() => {
                  navigateWithSearch('/tools/internal-affairs/new');
                }}
              >
                Add new
              </Button>
            </div>
          }
        />
      </div>

      <DeleteInternalAffairsCaseModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async (reason?: string) => {
          if (selectedCase?.id && reason) {
            try {
              await InternalAffairsApi.delete(selectedCase.id, reason);
              updateToast({
                type: ConfirmTypes.SUCCESS,
                open: true,
                message: 'IA Case deleted successfully',
              });
              setRefreshKey((prev) => prev + 1);
            } catch (error) {
              console.error('Error deleting case:', error);
              updateToast({
                open: true,
                message: 'Failed to delete IA case',
              });
            }
          }
          setIsDeleteModalOpen(false);
        }}
        caseData={
          selectedCase
            ? {
                caseId: selectedCase.caseId,
                caseType: formatCaseType(selectedCase.caseType),
                status: formatStatus(selectedCase.status),
                createdBy: selectedCase.createdBy,
              }
            : null
        }
      />

      <ExportAsPDFModal
        open={isExportPDFModalOpen}
        onClose={() => setIsExportPDFModalOpen(false)}
        onConfirm={async (exportData) => {
          if (selectedCase?.id) {
            await generateSingleCasePDF(
              selectedCase.id,
              'download',
              exportData,
            );
          }
          setIsExportPDFModalOpen(false);
        }}
        caseData={
          selectedCase
            ? {
                caseId: selectedCase.caseId,
                caseType: formatCaseType(selectedCase.caseType),
                status: formatStatus(selectedCase.status),
                createdBy: selectedCase.createdBy,
              }
            : null
        }
      />

      <SendEmailModal
        open={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        loading={emailSending}
        onConfirm={async (emailData) => {
          if (!selectedCase?.id) return;
          setEmailSending(true);
          try {
            // Generate PDF blob
            const pdfBlob = await generateSingleCasePDF(
              selectedCase.id,
              'blob',
              {
                recipient: emailData.recipient,
                reason: emailData.reason,
              },
            );
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
              console.error('Failed to generate PDF for email');
              return;
            }

            // Upload PDF to file storage
            const fileName = `${
              selectedCase.caseId || 'IA_Case'
            } - Emailed on ${new Date().toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })}.pdf`;
            const file = new File([pdfBlob], fileName, {
              type: 'application/pdf',
            });

            const uploadedFiles = await uploadFiles([
              {
                name: file.name,
                size: file.size,
                mimeType: file.type,
                origin: FileOrigin.EMAIL,
                file,
              },
            ]);

            if (uploadedFiles?.length > 0) {
              const uploadedFile = uploadedFiles[0];
              await InternalAffairsApi.emailFiles(selectedCase.id, {
                emailAddress: emailData.recipientEmail,
                subject: 'Internal Affairs Report',
                emailBody: emailData.emailBody,
                files: [{ ...uploadedFile, uploadedBy: undefined }],
              });

              updateToast({
                open: true,
                message: 'Email sent successfully!',
                type: ConfirmTypes.SUCCESS,
              });
            }

            setIsSendEmailModalOpen(false);
          } catch (error: any) {
            console.error('Error sending email with PDF:', error);
            updateToast({
              open: true,
              message: error?.message || 'Failed to send email',
              type: ConfirmTypes.ERROR,
            });
          } finally {
            setEmailSending(false);
          }
        }}
        caseData={
          selectedCase
            ? {
                caseId: selectedCase.caseId,
                caseType: formatCaseType(selectedCase.caseType),
                status: formatStatus(selectedCase.status),
                createdBy: selectedCase.createdBy,
              }
            : null
        }
      />

      <ExportReportModal
        open={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        reportData={reportData}
      />
    </>
  );
};

export default InternalAffairsTable;
